import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileText,
  Search,
  Plus,
  Folder,
  Copy,
  MoreHorizontal,
  Edit2,
  Trash2,
  LayoutGrid,
  List,
  Loader2,
  Library,
  Lock,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuthStore } from "@/store/auth.store";
import { clauseApi } from "../../services/api";

// Clause card component for grid view
const ClauseCard = ({ clause, onEdit, onDelete, t }) => (
  <Card className="group overflow-hidden hover:shadow-md transition-shadow">
    {/* Preview area */}
    <div className="h-32 bg-muted flex items-center justify-center relative">
      <FileText className="h-8 w-8 text-muted-foreground" />

      {/* Version badge */}
      {clause.version > 1 && (
        <span className="absolute bottom-2 left-2 text-xs text-muted-foreground bg-background/80 px-1.5 py-0.5 rounded">
          v{clause.version}
        </span>
      )}

      {/* Quick actions */}
      <div className="absolute top-2 right-2 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Edit2 className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>

    {/* Card content */}
    <CardContent className="p-4 dark:bg-background">
      {/* Title row with status badge */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="font-medium truncate">{clause.name}</h3>
        <Badge
          variant={clause.status === "approved" ? "default" : "secondary"}
          className={`shrink-0 text-xs ${
            clause.status === "approved"
              ? "bg-emerald-500 hover:bg-emerald-500"
              : ""
          }`}
        >
          {clause.status}
        </Badge>
      </div>

      {/* Content preview - fixed 3 lines height */}
      <div className="text-xs text-muted-foreground bg-muted rounded p-2 mb-3 font-mono h-18 overflow-hidden">
        <div className="line-clamp-3">{clause.content || " "}</div>
      </div>

      {/* Tags - fixed height */}
      <div className="flex flex-wrap gap-1 h-6 overflow-hidden">
        {clause.tags?.length > 0 ? (
          <>
            {clause.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {clause.tags.length > 3 && (
              <span className="text-xs text-muted-foreground">
                +{clause.tags.length - 3}
              </span>
            )}
          </>
        ) : (
          <span className="text-xs text-muted-foreground">
            {t("tags.noTags")}
          </span>
        )}
      </div>
    </CardContent>
  </Card>
);

// Clause row for list view
const ClauseListRow = ({ clause, onUse, onEdit, onDelete }) => (
  <TableRow>
    <TableCell className="font-medium">{clause.name}</TableCell>
    <TableCell className="text-muted-foreground max-w-xs truncate font-mono text-xs">
      {clause.content || "-"}
    </TableCell>
    <TableCell>
      <Badge
        variant={clause.status === "approved" ? "default" : "secondary"}
        className={
          clause.status === "approved"
            ? "bg-emerald-500 hover:bg-emerald-500"
            : ""
        }
      >
        {clause.status}
      </Badge>
    </TableCell>
    <TableCell>
      <div className="flex flex-wrap gap-1">
        {clause.tags?.slice(0, 2).map((tag) => (
          <Badge key={tag} variant="outline" className="text-xs">
            {tag}
          </Badge>
        ))}
        {clause.tags?.length > 2 && (
          <span className="text-xs text-muted-foreground">
            +{clause.tags.length - 2}
          </span>
        )}
      </div>
    </TableCell>
    <TableCell className="text-right">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onUse}>
            <Copy className="h-4 w-4 mr-2" />
            Copy
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onEdit}>
            <Edit2 className="h-4 w-4 mr-2" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onDelete} className="text-destructive">
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </TableCell>
  </TableRow>
);

// Empty state component
const EmptyState = ({ onCreateNew, t }) => (
  <div className="text-center py-12">
    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
    <h3 className="text-lg font-medium mb-2">{t("clauses.noClausesFound")}</h3>
    <p className="text-muted-foreground mb-4">
      Create reusable text clauses for your documents
    </p>
    <Button onClick={onCreateNew}>
      <Plus className="h-4 w-4 mr-2" />
      Create your first clause
    </Button>
  </div>
);

// Loading skeleton
const ClausesSkeleton = () => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <div>
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-64" />
      </div>
      <Skeleton className="h-10 w-32" />
    </div>
    <div className="flex gap-4">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-10 w-40" />
      <Skeleton className="h-10 w-40" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <Skeleton key={i} className="h-64" />
      ))}
    </div>
  </div>
);

// Create category dialog
const CategoryDialog = ({ open, onClose, onSaved }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");

  const createCategoryMutation = useMutation({
    mutationFn: (data) => clauseApi.createCategory(data),
    onSuccess: () => {
      toast.success(t("clauses.categoryCreated"));
      setName("");
      queryClient.invalidateQueries({ queryKey: ["clause-categories"] });
      onSaved();
    },
    onError: (error) => {
      console.error("Failed to create category:", error);
      toast.error(t("errors.generic"));
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t("clauses.categoryRequired"));
      return;
    }
    createCategoryMutation.mutate({ name });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("categories.create")}</DialogTitle>
          <DialogDescription>
            Add a new category to organize your clauses.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="categoryName">{t("categories.name")}</Label>
            <Input
              id="categoryName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Legal, HR, Finance"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createCategoryMutation.isPending}>
              {createCategoryMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                t("common.create")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// UpgradeRequired Component
// ============================================
const UpgradeRequired = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("clauses.title", "Clause Library")}
        </h1>
        <p className="text-muted-foreground">
          {t("clauses.subtitle", "Store and reuse common document clauses")}
        </p>
      </div>

      {/* Single Card with divider */}
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6">
            <Lock className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">
            {t("upgrade.featureNotAvailable", "Feature Not Available")}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            {t(
              "upgrade.clauseLibraryUpgradeDesc",
              "The Clause Library lets you save commonly used text snippets, legal clauses, and boilerplate content.",
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => navigate("/settings/billing")}>
              <Sparkles className="h-4 w-4 mr-2" />
              {t("upgrade.upgradePlan", "Upgrade Plan")}
            </Button>
            <Button variant="outline" onClick={() => navigate("/envelopes")}>
              {t("common.backToEnvelopes", "Back to Envelopes")}
            </Button>
          </div>

          {/* Feature highlights */}
          <div className="mt-8 pt-8 border-t w-full max-w-lg">
            <div className="w-full flex items-center justify-center gap-2 mb-4">
              <Library className="h-3.5 w-3.5 text-primary" />
              <h3 className="text-sm font-medium text-muted-foreground">
                {t(
                  "clauses.withClauseLibraryYouCan",
                  "With Clause Library you can:",
                )}
              </h3>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 flex items-center justify-center flex-col">
              <li>
                {t(
                  "clauses.feature1",
                  "Save commonly used text snippets and clauses",
                )}
              </li>
              <li>{t("clauses.feature2", "Organize clauses by categories")}</li>
              <li>{t("clauses.feature3", "Quick insertion into documents")}</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main component
export const ClauseLibrary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Get user subscription to check feature access
  const user = useAuthStore((state) => state.user);

  // Check if user has clause library feature - must be explicitly true
  const hasClauseLibraryFeature =
    user?.subscription?.features?.clauseLibrary === true;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // Memoize query params
  const clauseParams = useMemo(
    () => ({
      categoryId: selectedCategory !== "all" ? selectedCategory : undefined,
      search: searchQuery || undefined,
      status: statusFilter !== "all" ? statusFilter : undefined,
    }),
    [selectedCategory, searchQuery, statusFilter],
  );

  // Fetch categories (only if feature enabled)
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ["clause-categories"],
    queryFn: async () => {
      const response = await clauseApi.getCategories();
      const cats = response.data?.data;
      return Array.isArray(cats) ? cats : cats?.flat || [];
    },
    enabled: hasClauseLibraryFeature, // Don't fetch if feature is disabled
  });

  // Fetch clauses (only if feature enabled)
  const { data: clausesData, isLoading: clausesLoading } = useQuery({
    queryKey: ["clauses", clauseParams],
    queryFn: async () => {
      const response = await clauseApi.list(clauseParams);
      return response.data?.data?.items || [];
    },
    placeholderData: (prev) => prev,
    enabled: hasClauseLibraryFeature, // Don't fetch if feature is disabled
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (clauseId) => clauseApi.delete(clauseId),
    onSuccess: () => {
      toast.success(t("clauses.deleted"));
      queryClient.invalidateQueries({ queryKey: ["clauses"] });
    },
    onError: (error) => {
      console.error("Failed to delete clause:", error);
      toast.error(t("errors.generic"));
    },
  });

  const categories = categoriesData || [];
  const clauses = clausesData || [];
  const loading = categoriesLoading || clausesLoading;

  const handleUseClause = async (clause) => {
    try {
      const response = await clauseApi.use(clause.id);
      const content = response.data?.data?.content || clause.content;
      await navigator.clipboard.writeText(content);
      toast.success(t("clauses.copied"));
    } catch (error) {
      console.error("Failed to use clause:", error);
      // Fallback: copy content directly
      try {
        await navigator.clipboard.writeText(clause.content);
        toast.success(t("clauses.copied"));
      } catch {
        toast.error(t("errors.generic"));
      }
    }
  };

  const handleDeleteClause = (clauseId) => {
    if (!confirm(t("clauses.confirmDelete"))) return;
    deleteMutation.mutate(clauseId);
  };

  const handleEditClause = (clause) => {
    navigate(`/clause-library/${clause.id}/edit`);
  };

  const handleCreateClause = () => {
    navigate("/clause-library/new/edit");
  };

  const handleCategorySaved = () => {
    setShowCategoryModal(false);
  };

  const renderContent = () => {
    if (clauses.length === 0) {
      return (
        <Card>
          <CardContent className="p-0">
            <EmptyState onCreateNew={handleCreateClause} t={t} />
          </CardContent>
        </Card>
      );
    }
    return viewMode === "grid" ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {clauses.map((clause) => (
          <ClauseCard
            key={clause.id}
            clause={clause}
            t={t}
            onEdit={() => handleEditClause(clause)}
            onDelete={() => handleDeleteClause(clause.id)}
          />
        ))}
      </div>
    ) : (
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("common.name")}</TableHead>
              <TableHead>{t("common.content")}</TableHead>
              <TableHead>{t("common.status")}</TableHead>
              <TableHead>{t("common.tags")}</TableHead>
              <TableHead className="w-12.5" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {clauses.map((clause) => (
              <ClauseListRow
                key={clause.id}
                clause={clause}
                onUse={() => handleUseClause(clause)}
                onEdit={() => handleEditClause(clause)}
                onDelete={() => handleDeleteClause(clause.id)}
              />
            ))}
          </TableBody>
        </Table>
      </Card>
    );
  };

  // ============================================
  // Feature Check - Show upgrade message if clause library not available
  // ============================================

  if (!hasClauseLibraryFeature) {
    return <UpgradeRequired />;
  }

  if (loading && !clausesData) {
    return <ClausesSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            Clause Library
          </h1>
          <p className="text-muted-foreground">
            Create and manage reusable text clauses for your documents
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCategoryModal(true)}>
            <Folder className="h-4 w-4 mr-2" />
            Add Category
          </Button>
          <Button onClick={handleCreateClause}>
            <Plus className="h-4 w-4 mr-2" />
            Add Clause
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center flex-1">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("placeholders.searchClauses")}
              className="pl-10"
            />
          </div>

          {/* Category filter */}
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-45">
              <SelectValue placeholder={t("placeholders.allCategories")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("filters.allCategories")}</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-35">
              <SelectValue placeholder={t("placeholders.allStatus")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("filters.allStatus")}</SelectItem>
              <SelectItem value="draft">{t("common.draft")}</SelectItem>
              <SelectItem value="approved">{t("common.approved")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* View toggle */}
        <div className="flex items-center border rounded-lg p-1">
          <Button
            variant={viewMode === "grid" ? "secondary" : "ghost"}
            size="sm"
            className="h-8 px-2"
            onClick={() => setViewMode("grid")}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "secondary" : "ghost"}
            size="sm"
            className="h-8 px-2"
            onClick={() => setViewMode("list")}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Clauses grid/list */}
      {renderContent()}

      {/* Create category dialog */}
      <CategoryDialog
        open={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onSaved={handleCategorySaved}
      />
    </div>
  );
};

export default ClauseLibrary;
